import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { FiHeart } from 'react-icons/fi';

/**
 * Cohort resume health panel. Mirrors the user-side ResumeEvolution layout
 * (line on the left, radar on the right) but uses cohort averages and adds
 * a third widget: a 5-bucket distribution of mentees' latest scores so the
 * mentor can see the spread, not just the mean.
 */
const BIN_COLORS = ['#fb7185', '#fbbf24', '#94a3b8', '#86efac', '#10b981'];

const LineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm text-xs">
      <p className="font-medium text-gray-700 mb-1">
        Week of {new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </p>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-primary-500" />
        <span className="text-gray-500">Avg score:</span>
        <span className="font-semibold text-gray-800">{payload[0].value}</span>
      </div>
    </div>
  );
};

const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const CohortResumeHealth = ({ data }) => {
  const safe = data || {};
  const scoresOverTime = Array.isArray(safe.scoresOverTime) ? safe.scoresOverTime : [];
  const currentRadar = Array.isArray(safe.currentRadar) ? safe.currentRadar : [];
  const distribution = Array.isArray(safe.distribution) ? safe.distribution : [];
  const menteesWithDiagnoses = safe.menteesWithDiagnoses ?? 0;

  const first = scoresOverTime[0]?.overall ?? 0;
  const latest = scoresOverTime[scoresOverTime.length - 1]?.overall ?? 0;
  const improvement = latest - first;
  const hasAnyData = scoresOverTime.length > 0 || currentRadar.some((r) => r.score > 0);

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-primary-500 h-full flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Cohort Resume Health</h3>
          <p className="text-xs text-gray-500">
            {menteesWithDiagnoses > 0
              ? `Average across ${menteesWithDiagnoses} mentee${menteesWithDiagnoses === 1 ? '' : 's'} with diagnoses`
              : 'Run resume diagnoses with mentees to populate this view'}
          </p>
        </div>
        {scoresOverTime.length > 1 && (
          <div className={`flex items-center gap-1.5 rounded-lg px-2 py-1 ${improvement >= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
            <span className={`text-xs font-semibold ${improvement >= 0 ? 'text-green-700' : 'text-orange-600'}`}>
              {improvement >= 0 ? '+' : ''}{improvement} pts
            </span>
            <span className={`text-[10px] ${improvement >= 0 ? 'text-green-600' : 'text-orange-500'}`}>
              since start
            </span>
          </div>
        )}
      </div>

      {!hasAnyData ? (
        <div className="py-10 text-center text-xs text-gray-400 flex-1 flex flex-col items-center justify-center">
          <FiHeart className="h-6 w-6 text-gray-300 mb-2" />
          No resume diagnoses recorded for your mentees yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 flex-1 min-h-0 mt-2">
          <div className="lg:col-span-3 min-w-0 flex flex-col">
            <p className="text-[11px] font-semibold text-gray-700 mb-1">Average score over time</p>
            {scoresOverTime.length > 0 ? (
              <div className="flex-1 min-h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={scoresOverTime} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <Tooltip content={<LineTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="overall"
                      stroke="#5AAFA7"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: '#5AAFA7' }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-xs text-gray-400">
                No diagnosis history yet.
              </div>
            )}
          </div>

          <div className="lg:col-span-2 lg:pl-4 lg:border-l lg:border-gray-100 flex flex-col">
            <p className="text-[11px] font-semibold text-gray-700 mb-1">Average breakdown</p>
            {currentRadar.length > 0 ? (
              <div className="flex-1 min-h-[180px] flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={currentRadar}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 9, fill: '#64748b' }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar
                      name="Cohort"
                      dataKey="score"
                      stroke="#5AAFA7"
                      fill="#5AAFA7"
                      fillOpacity={0.25}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-xs text-gray-400">
                Add diagnoses to populate.
              </div>
            )}
          </div>

          <div className="lg:col-span-1 lg:pl-3 lg:border-l lg:border-gray-100 flex flex-col">
            <p className="text-[11px] font-semibold text-gray-700 mb-1">Score spread</p>
            {distribution.length > 0 ? (
              <div className="flex-1 min-h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={distribution}
                    layout="vertical"
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="range"
                      tick={{ fontSize: 9, fill: '#94a3b8' }}
                      width={40}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                      formatter={(value) => [`${value} mentee${value === 1 ? '' : 's'}`, 'Count']}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {distribution.map((_, i) => (
                        <Cell key={i} fill={BIN_COLORS[i] || '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-xs text-gray-400">—</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CohortResumeHealth;
